import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  DialogContentText,
  TablePagination,
  TableSortLabel,
  Skeleton,
  Snackbar,
  Checkbox,
  ListItemText,
  Autocomplete,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, Download, Upload, CheckCircle, Tune as TuneIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { downloadCsvTemplate, TASK_TEMPLATE_COLUMNS } from '../utils/csvTemplate';
import { normalizeDate, splitList } from '../components/bulk/helpers';
import useDebounce from '../hooks/useDebounce';
import Linkify from 'react-linkify';
import ToolbarButton from '../components/ToolbarButton';
import { WaveLoading } from '../components/Loader';
import StatusPill from '../components/StatusPill';
import { getProjectDisplayKey } from '../utils/projectDisplay';

// Loaded on demand: SheetJS is ~150 kB gzipped and only needed for bulk entry.
const BulkGrid = React.lazy(() => import('../components/bulk/BulkGrid'));

const TaskManagement = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const { canPerformActionByRoute, moduleIdMap, loading: permissionsLoading } = usePermissions();
  const tasksModuleId = moduleIdMap['Tasks'];
  
  // Permission checks
  const canAdd = user?.role === 'admin' || canPerformActionByRoute('/tasks', 'Add');
  const canEdit = user?.role === 'admin' || canPerformActionByRoute('/tasks', 'Edit');
  const canDelete = user?.role === 'admin' || canPerformActionByRoute('/tasks', 'Delete');
  const canView = user?.role === 'admin' || canPerformActionByRoute('/tasks', 'View');
  
  // Field visibility state
  const [hiddenFields, setHiddenFields] = useState([]);
  
  // Fetch field visibility for Tasks module
  useEffect(() => {
    const fetchFieldVisibility = async () => {
      if (!user?.serviceid || !tasksModuleId) {
        setHiddenFields([]);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/service-management/field-visibility/${user.serviceid}/${tasksModuleId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setHiddenFields(data.hiddenFields || []);
        } else {
          setHiddenFields([]);
        }
      } catch (error) {
        console.error('Error fetching field visibility:', error);
        setHiddenFields([]);
      }
    };
    
    fetchFieldVisibility();
  }, [user?.serviceid, tasksModuleId]);
  
  // Add constants after hooks
  const MARCOM_SERVICE_IDS = [35]; // Known Marcom service IDs
  
  // Helper to check if user is from Marcom service - simple check using serviceid
  const isUserMarcom = () => {
    if (user?.role === 'admin') return true;
    if (user?.serviceid && MARCOM_SERVICE_IDS.includes(Number(user.serviceid))) return true;
    return false;
  };

  // Helper to check if a field is hidden
  const isFieldHidden = (fieldName) => {
    // For filelocation field, always show it for Marcom users and admins, regardless of hiddenFields
    if (fieldName === 'filelocation') {
      const isMarcom = isUserMarcom();
      if (isMarcom) {
        console.log('[TaskManagement] filelocation field visible for Marcom user. user.role:', user?.role, 'user.serviceid:', user?.serviceid, 'marcomServiceId:', marcomServiceId);
        return false; // Always show for Marcom users
      }
      console.log('[TaskManagement] filelocation field hidden. isMarcom:', isMarcom, 'user.serviceid:', user?.serviceid, 'marcomServiceId:', marcomServiceId, 'hiddenFields:', hiddenFields);
    }
    // For intervenor field, always show it for Marcom users and admins, regardless of hiddenFields
    if (fieldName === 'intervenor') {
      const isMarcom = isUserMarcom();
      if (isMarcom) {
        return false; // Always show for Marcom users and admins
      }
      return true; // Hide for non-Marcom users
    }
    return hiddenFields.includes(fieldName);
  };
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [meetingLookup, setMeetingLookup] = useState([]);
  const [trainingLookup, setTrainingLookup] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [filters, setFilters] = useState({
    taskName: '',
    projectName: '',
    status: '',
    serviceId: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [userServiceName, setUserServiceName] = useState(null);
  const [isMarcomService, setIsMarcomService] = useState(false);
  const [marcomServiceId, setMarcomServiceId] = useState(null);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 5, totalPages: 0 });
  const [taskType, setTaskType] = useState('regular'); // 'regular', 'meeting', 'training'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    milestone: '',
    userStory: '',
    sprint: '',
    assignedTo: [],
    ownerId: '',
    qcId: '',
    status: 'todo',
    priority: 'medium',
    estimatedHours: '',
    deadline: '',
    estimatedTransactions: '',
    transactionType: '',
    filelocation: '',
    intervenor: [],
  });
  const [qcUsers, setQcUsers] = useState([]);
  const [marcomUsers, setMarcomUsers] = useState([]);
  const [loadingMarcomUsers, setLoadingMarcomUsers] = useState(false);
  const [milestoneOptions, setMilestoneOptions] = useState([]);
  const [userStoryOptions, setUserStoryOptions] = useState([]);
  const [sprintOptions, setSprintOptions] = useState([]);
  const [bulkGridOpen, setBulkGridOpen] = useState(false);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [loadingTransactionTypes, setLoadingTransactionTypes] = useState(false);
  const [taskStatus, settaskStatus] = useState([]);
  const [loadingTaskStatus, setLoadingTaskStatus] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [touchedFields, setTouchedFields] = useState({});
  const [currentErrorField, setCurrentErrorField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getFirstMissingRequired = (data, isSubmittingForReview = false) => {
    if (!isFieldHidden('title') && !data.title) return 'title';
    if (!isFieldHidden('projectId') && !data.projectId) return 'projectId';
    if (!isFieldHidden('description') && !data.description) return 'description';
    if (!isFieldHidden('ownerId') && !data.ownerId) return 'ownerId';
    if (!isFieldHidden('assignedTo') && (!data.assignedTo || data.assignedTo.length === 0)) return 'assignedTo';
    // Validate filelocation for Marcom users (or admins) when submitting for review
    const canSeeFileLocation = isUserMarcom() && !isFieldHidden('filelocation');
    if (canSeeFileLocation) {
      const statusIsSubmitted = data.status === 'submitted_for_review' || data.status === 'Submitted for Review';
      if ((isSubmittingForReview || statusIsSubmitted) && (!data.filelocation || !data.filelocation.trim())) {
        return 'filelocation';
      }
    }
    return null;
  };

  // Filter projects based on user role
  const getFilteredProjects = () => {
    if (user.role === 'admin') {
      return projects; // Admin sees all projects
    }
    
    if (user.role === 'project_manager') {
      // PM sees projects they manage or are part of
      return projects.filter(project => 
        project.managerId === user.id || 
        (project.teamMembers && project.teamMembers.some(member => member.id === user.id))
      );
    }
    
    if (user.role === 'team_leader') {
      // TL sees projects they lead or are part of
      return projects.filter(project => 
        project.teamLeaderId === user.id || 
        (project.teamMembers && project.teamMembers.some(member => member.id === user.id))
      );
    }
    
    if (user.role === 'team_member') {
      // Team member sees projects they are assigned to
      return projects.filter(project => 
        project.teamMembers && project.teamMembers.some(member => member.id === user.id)
      );
    }
    
    return projects; // Fallback to all projects
  };

  // Filter users based on selected project
  const getProjectRelatedUsers = () => {
    if (!formData.projectId) {
      return users; // Return all users if no project selected
    }
    
    // Find the selected project
    const selectedProject = projects.find(p => p.id === parseInt(formData.projectId));
    if (!selectedProject) {
      return users; // Return all users if project not found
    }
    
    // Get users related to the project
    const projectUserIds = new Set();
    
    // Add project manager
    if (selectedProject.managerId) {
      projectUserIds.add(selectedProject.managerId);
    }
    
    // Add team leader
    if (selectedProject.teamLeaderId) {
      projectUserIds.add(selectedProject.teamLeaderId);
    }
    
    // Add team members
    if (selectedProject.teamMembers) {
      selectedProject.teamMembers.forEach(member => {
        projectUserIds.add(member.id);
      });
    }
    
    // Filter users to only include those related to the project
    return users.filter(user => projectUserIds.has(user.id));
  };

  useEffect(() => {
    if (user) {
      fetchTasks(1, 10000);
      fetchDistinctTaskFields();
      fetchProjects();
      fetchMeetingLookup();
      fetchTrainingLookup();
      // Always fetch users — needed for bulk upload validation (resolving owner/assignee emails to IDs)
      fetchUsers();
      // Fetch services for all users (needed to determine labels based on project service)
      fetchServices();
      // Fetch user's service name for label customization
      if (user.serviceid) {
        fetchUserServiceName();
      }
      // Fetch Marcom users if user is Marcom or admin
      if (isUserMarcom()) {
        fetchMarcomUsers();
      }
    }
  }, [user]);

  const fetchTasks = async (pageNum = 1, limit = 10000) => {
    try {
      setLoadingTasks(true);
      const endpoint = user && user.role === 'team_member'
        ? `${process.env.REACT_APP_API_URL}/tasks/my-tasks`
        : `${process.env.REACT_APP_API_URL}/tasks`;
      const url = `${endpoint}?page=${pageNum}&limit=${limit}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}: ${response.statusText}` }));
        console.error('[TaskManagement] Failed to fetch tasks:', response.status, errorData);
        throw new Error(errorData.message || `Failed to fetch tasks: ${response.status}`);
      }
      const data = await response.json();
      console.log('[TaskManagement] Fetched tasks response:', data);
      const tasksData = Array.isArray(data) ? data : data.data || [];
      console.log('[TaskManagement] Tasks count:', tasksData.length);
      // Log rejection reasons for debugging
      const rejectedTasks = tasksData.filter(t => t.status === 'rejected');
      if (rejectedTasks.length > 0) {
        console.log('[TaskManagement] Rejected tasks with reasons:', rejectedTasks.map(t => ({
          id: t.id,
          title: t.title,
          status: t.status,
          hasRejectionReason: !!t.rejectionReason,
          rejectionReason: t.rejectionReason?.substring(0, 50)
        })));
      }
      setTasks(tasksData);
      // when loading with high limit, we ignore server pagination metadata
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setError('Failed to fetch tasks');
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchDistinctTaskFields = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tasks/distinct-fields`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      setMilestoneOptions(Array.isArray(data.milestones) ? data.milestones : []);
      setUserStoryOptions(Array.isArray(data.userStories) ? data.userStories : []);
      setSprintOptions(Array.isArray(data.sprints) ? data.sprints : []);
    } catch (error) {
      console.error('Error fetching distinct task fields:', error);
    }
  };

  // ---- Bulk grid (paste / import / type) --------------------------------------
  const bulkColumns = useMemo(() => {
    const projectNames = projects.map((p) => p.name).filter(Boolean);
    const emails = users.map((u) => u.email).filter(Boolean).sort();
    const cols = [
      { key: 'projectName', label: 'Project', type: 'lookup', required: true, width: 220, lookupOptions: projectNames, aliases: ['project name', 'project'], help: 'An existing project you can see. Type to search.' },
      { key: 'title', label: 'Task name', type: 'text', required: true, width: 220, aliases: ['task', 'task title', 'name', 'title'] },
      { key: 'description', label: 'Description', type: 'text', required: true, width: 240, aliases: ['desc', 'details'] },
      { key: 'ownerEmail', label: 'Owner email', type: 'lookup', width: 220, lookupOptions: emails, emails: true, aliases: ['owner', 'owner email', 'created by', 'raised by', 'requested by', 'reporter'], help: 'The person responsible for the task. Blank means you.' },
      { key: 'assigneeEmails', label: 'Assignees', type: 'multilookup', required: true, width: 260, lookupOptions: emails, emails: true, inferPriority: 1, aliases: ['assignee', 'assignee emails', 'assigned to', 'assign to', 'resources', 'resource', 'allocated to', 'team member', 'members', 'developer'], placeholder: 'emails…', help: 'One or more emails. They must be on the project team.' },
      { key: 'status', label: 'Status', type: 'select', width: 130, options: [{ value: 'todo', label: 'To Do' }, { value: 'in_progress', label: 'In Progress' }], aliases: ['state', 'stage'], help: 'Blank means To Do.',
        synonyms: { 'to do': 'todo', 'to-do': 'todo', open: 'todo', new: 'todo', pending: 'todo', 'not started': 'todo', backlog: 'todo', wip: 'in_progress', 'in-progress': 'in_progress', inprogress: 'in_progress', started: 'in_progress', ongoing: 'in_progress', active: 'in_progress', 'work in progress': 'in_progress' } },
      { key: 'priority', label: 'Priority', type: 'select', width: 120, options: [{ value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }], aliases: ['prio', 'urgency', 'importance'], help: 'Blank means Medium.',
        synonyms: { l: 'low', lo: 'low', minor: 'low', p3: 'low', p4: 'low', m: 'medium', med: 'medium', normal: 'medium', moderate: 'medium', p2: 'medium', h: 'high', hi: 'high', urgent: 'high', critical: 'high', major: 'high', p1: 'high', p0: 'high' } },
      { key: 'deadline', label: 'Deadline', type: 'date', width: 150, aliases: ['due', 'due date', 'end date'], placeholder: 'dd/mm/yyyy' },
      { key: 'estimatedHours', label: 'Est. hours', type: 'number', width: 100, aliases: ['estimate', 'estimated hours', 'hours'] },
      { key: 'estimatedTransactions', label: 'Est. transactions', type: 'number', width: 120, aliases: ['transactions', 'estimated transactions', 'tx'] },
      { key: 'milestone', label: 'Milestone', type: 'lookup', width: 150, lookupOptions: milestoneOptions },
      { key: 'userStory', label: 'User story', type: 'lookup', width: 150, lookupOptions: userStoryOptions, aliases: ['story'] },
      { key: 'sprint', label: 'Sprint', type: 'lookup', width: 120, lookupOptions: sprintOptions },
      { key: 'transactionType', label: isUserMarcom() ? 'Format' : 'Transaction type', type: 'text', width: 150, aliases: ['type', 'format', 'transaction type'], help: 'Must be one of the types set up for the project\'s service.' },
    ];
    if (isUserMarcom()) cols.push({ key: 'filelocation', label: 'File location', type: 'text', width: 200, aliases: ['file', 'location', 'path'] });
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, users, milestoneOptions, userStoryOptions, sprintOptions, user?.serviceid]);

  const validateTaskRow = useCallback((v, ctx) => {
    const errors = {};
    const lc = (x) => String(x || '').trim().toLowerCase();
    const project = projects.find((p) => lc(p.name) === lc(v.projectName) || lc(getProjectDisplayKey(p)) === lc(v.projectName));
    if (v.projectName && !project) errors.projectName = `No project called "${v.projectName}"`;
    const owner = users.find((u) => lc(u.email) === lc(v.ownerEmail));
    if (v.ownerEmail && !owner && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.ownerEmail.trim())) errors.ownerEmail = 'Not an email address';
    const assigneeEmails = splitList(v.assigneeEmails);
    const assigneeIds = []; const unresolved = [];
    assigneeEmails.forEach((e) => { const u = users.find((x) => lc(x.email) === lc(e)); if (u) assigneeIds.push(u.id); else unresolved.push(e); });
    const badEmail = assigneeEmails.find((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (badEmail) errors.assigneeEmails = `"${badEmail}" is not an email address`;
    const deadline = v.deadline ? normalizeDate(v.deadline, ctx.dayFirst) : null;
    if (v.deadline && !deadline) errors.deadline = 'Not a date';
    const estimatedHours = v.estimatedHours ? parseFloat(v.estimatedHours) : null;
    const estimatedTransactions = v.estimatedTransactions ? parseInt(v.estimatedTransactions, 10) : null;
    return {
      errors,
      payload: {
        projectId: project ? project.id : null,
        title: String(v.title || '').trim(),
        description: String(v.description || '').trim(),
        milestone: v.milestone || null, userStory: v.userStory || null, sprint: v.sprint || null,
        ownerId: owner ? owner.id : null,
        ownerEmail: owner ? null : (v.ownerEmail || '').trim() || null, // backend resolves emails outside my scope
        assignedTo: assigneeIds,
        assigneeEmails: unresolved,
        status: v.status || 'todo',
        priority: (v.priority || 'medium').toLowerCase(),
        estimatedHours: estimatedHours || 0,
        estimatedTransactions: estimatedTransactions || 0,
        deadline,
        transactionType: (v.transactionType || '').trim() || null,
        filelocation: v.filelocation || null,
        IsTask: true,
      },
    };
  }, [projects, users]);

  const taskDedupeKey = useCallback((v) => (v.projectName && v.title ? `${v.projectName.trim().toLowerCase()}|${v.title.trim().toLowerCase()}` : null), []);

  const postBulkTasks = async (payloads, dryRun) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/tasks/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ tasks: payloads, dryRun }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Bulk upload failed');
    return data;
  };
  const bulkDryRun = (payloads) => postBulkTasks(payloads, true);
  const bulkSubmit = async (payloads) => {
    const data = await postBulkTasks(payloads, false);
    fetchTasks(1, 10000);
    fetchDistinctTaskFields();
    return data;
  };

  const fetchProjects = async () => {
    try {
      // Request all projects for dropdown (same as other modules)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects?page=1&limit=10000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      setProjects(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchServices = async () => {
    try {
      setLoadingServices(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/services`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Unknown error' }));
        console.error('[TaskManagement] Failed to fetch services:', res.status, errorData);
        throw new Error(errorData.message || 'Failed to fetch services');
      }
      
      const data = await res.json();
      console.log('[TaskManagement] Fetched services:', data.length, 'services');
      setServices(data);
    } catch (err) {
      console.error('[TaskManagement] Fetch services error:', err);
      // Don't fail silently - set empty array
      setServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const fetchUserServiceName = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/services`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('[TaskManagement] Fetched services for user service name:', data.length, 'services');
        
        // Find Marcom serviceId
        const marcomService = data.find(service => service.Service && service.Service.toLowerCase().trim() === 'marcom');
        if (marcomService) {
          setMarcomServiceId(marcomService.Id);
          console.log('[TaskManagement] Marcom serviceId found:', marcomService.Id, 'Type:', typeof marcomService.Id);
          console.log('[TaskManagement] User serviceid:', user.serviceid, 'Type:', typeof user.serviceid);
          console.log('[TaskManagement] Match check:', Number(marcomService.Id) === Number(user.serviceid));
        } else {
          console.warn('[TaskManagement] Marcom service not found in services list. Available services:', data.map(s => `${s.Id}:${s.Service}`));
        }
        
        const userService = data.find(service => service.Id === user.serviceid);
        if (userService) {
          console.log('[TaskManagement] User service found:', userService.Service, 'for serviceId:', user.serviceid);
          setUserServiceName(userService.Service);
          
          // Check if user's service is Marcom and set label flag accordingly
          const serviceNameLower = userService.Service.toLowerCase().trim();
          if (serviceNameLower === 'marcom' || serviceNameLower.includes('marcom')) {
            console.log('[TaskManagement] User is from Marcom service - setting isMarcomService to true');
            setIsMarcomService(true);
          } else {
            setIsMarcomService(false);
          }
        } else {
          console.warn('[TaskManagement] Service not found for serviceId:', user.serviceid, 'Available services:', data.map(s => `${s.Id}:${s.Service}`));
          setIsMarcomService(false);
        }
      } else {
        const errorData = await res.json().catch(() => ({ message: 'Unknown error' }));
        console.error('[TaskManagement] Failed to fetch services for user service name:', res.status, errorData);
        setIsMarcomService(false);
      }
    } catch (err) {
      console.error('[TaskManagement] Fetch user service name error:', err);
    }
  };

  // Helper function to get label based on service
  // Simplified: Check user's service at login time, not dynamically per project
  const getTransactionTypeLabel = () => {
    return isUserMarcom() ? 'Format' : 'Transaction Type';
  };

  // Helper function to check if rejection columns should be shown
  // Show if user's service is Marcom OR if any task in the list belongs to Marcom service
  const shouldShowRejectionColumns = () => {
    if (userServiceName === 'Marcom') {
      return true;
    }
    // Check if any task has a Marcom project (check project.serviceId or project name)
    return tasks.some(task => {
      const project = projects.find(p => p.id === task.projectId);
      // We'll check by service name if available, or we can check serviceId
      // For now, we'll rely on backend filtering - if rejectionCount > 0 or rejectionReason exists, it's Marcom
      return task.rejectionCount > 0 || task.rejectionReason;
    });
  };

  const fetchUsers = async (includeAssignedIds = []) => {
    try {
      // Use project management specific endpoint that bypasses teamScope restrictions
      let url = `${process.env.REACT_APP_API_URL}/users/for-project-management`;
      
      // If we have assigned user IDs to include, add them to the query
      if (includeAssignedIds.length > 0) {
        const assignedIdsParam = includeAssignedIds.join(',');
        url += `?includeAssigned=${assignedIdsParam}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback to active users if project management endpoint fails
      try {
        const fallbackUrl = `${process.env.REACT_APP_API_URL}/users/active`;
        const fallbackResponse = await fetch(fallbackUrl, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          setUsers(Array.isArray(fallbackData) ? fallbackData : fallbackData.data || []);
        }
      } catch (fallbackError) {
        console.error('Fallback user fetch failed:', fallbackError);
      }
    }
  };

  const fetchProjectTeamMembers = async (projectId, includeAssignedIds = []) => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/projects/${projectId}/team`;
      
      // If we have assigned user IDs to include (for editing), use the special endpoint
      if (includeAssignedIds.length > 0) {
        const assignedIdsParam = includeAssignedIds.join(',');
        url = `${process.env.REACT_APP_API_URL}/projects/${projectId}/team-with-assigned?assignedIds=${assignedIdsParam}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch project team members');
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching project team members:', error);
    }
  };

  // Optimized function to fetch users for PMs/TLs
  const fetchUsersOptimized = async (includeAssignedIds = [], projectId = null) => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/users/for-project-management`;
      
      if (projectId) {
        // Use project-specific endpoint for better filtering when project is selected
        url = `${process.env.REACT_APP_API_URL}/projects/${projectId}/team`;
        if (includeAssignedIds.length > 0) {
          const assignedIdsParam = includeAssignedIds.join(',');
          url = `${process.env.REACT_APP_API_URL}/projects/${projectId}/team-with-assigned?assignedIds=${assignedIdsParam}`;
        }
      } else {
        // Use project management endpoint for general user fetching
        if (includeAssignedIds.length > 0) {
          const assignedIdsParam = includeAssignedIds.join(',');
          url += `?includeAssigned=${assignedIdsParam}`;
        }
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMeetingLookup = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tasks/meeting-lookup`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch meeting lookup');
      const data = await response.json();
      setMeetingLookup(data);
    } catch (error) {
      console.error('Error fetching meeting lookup:', error);
    }
  };

  const fetchTrainingLookup = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tasks/training-lookup`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch training lookup');
      const data = await response.json();
      setTrainingLookup(data);
    } catch (error) {
      console.error('Error fetching training lookup:', error);
    }
  };

  const fetchQCsByService = async (serviceId) => {
    try {
      if (!serviceId) {
        setQcUsers([]);
        return;
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tasks/qcs-by-service/${serviceId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch QC users');
      const data = await response.json();
      setQcUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching QC users:', error);
      setQcUsers([]);
    }
  };

  const fetchMarcomUsers = async () => {
    try {
      setLoadingMarcomUsers(true);
      console.log('[TaskManagement] Fetching Marcom users...');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tasks/marcom-users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      console.log('[TaskManagement] Marcom users response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('[TaskManagement] Failed to fetch Marcom users:', errorData);
        throw new Error(errorData.message || 'Failed to fetch Marcom users');
      }
      
      const data = await response.json();
      console.log('[TaskManagement] Marcom users data received:', data);
      console.log('[TaskManagement] Marcom users count:', Array.isArray(data) ? data.length : 0);
      
      const usersArray = Array.isArray(data) ? data : [];
      setMarcomUsers(usersArray);
      
      if (usersArray.length === 0) {
        console.warn('[TaskManagement] No Marcom users found');
      }
    } catch (error) {
      console.error('[TaskManagement] Error fetching Marcom users:', error);
      setMarcomUsers([]);
    } finally {
      setLoadingMarcomUsers(false);
    }
  };

  const fetchTransactionTypes = async (serviceId) => {
    try {
      setLoadingTransactionTypes(true);
      if (!serviceId) {
        console.log('[TaskManagement] No serviceId provided, clearing transaction types');
        setTransactionTypes([]);
        settaskStatus([]);
        return;
      }
      console.log('[TaskManagement] Fetching transaction types for serviceId:', serviceId);
      const url = `${process.env.REACT_APP_API_URL}/transaction-types?serviceId=${serviceId}`;
      console.log('[TaskManagement] Fetching from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      console.log('[TaskManagement] Response status:', response.status);
      console.log('[TaskManagement] Response ok:', response.ok);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('[TaskManagement] Error response:', errorData);
        throw new Error(errorData.message || 'Failed to fetch transaction types');
      }
      
      const data = await response.json();
      console.log('[TaskManagement] Received data:', data);
      console.log('[TaskManagement] Data type:', Array.isArray(data) ? 'array' : typeof data);
      console.log('[TaskManagement] Data length:', Array.isArray(data) ? data.length : 'N/A');
      
      const transactionTypesArray = Array.isArray(data) ? data : [];
      console.log('[TaskManagement] Setting transaction types:', transactionTypesArray);
      setTransactionTypes(transactionTypesArray);
    } catch (error) {
      console.error('[TaskManagement] Error fetching transaction types:', error);
      console.error('[TaskManagement] Error details:', error.message);
      setTransactionTypes([]);
    } finally {
      setLoadingTransactionTypes(false);
    }
  };
  const fetchTaskStatus = async (serviceId) => {
    try {
      setLoadingTaskStatus(true);
      if (!serviceId) {
        console.log('[TaskManagement] No serviceId provided, clearing task status');
       settaskStatus([]);
        return;
      }
      console.log('[TaskManagement] Fetching task status for serviceId:', serviceId);
      const url = `${process.env.REACT_APP_API_URL}/task-status?serviceId=${serviceId}`;
      console.log('[TaskManagement] Fetching from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      console.log('[TaskManagement] Response status:', response.status);
      console.log('[TaskManagement] Response ok:', response.ok);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('[TaskManagement] Error response:', errorData);
        throw new Error(errorData.message || 'Failed to fetch task status');
      }
      
      const data = await response.json();
      console.log('[TaskManagement] Received data:', data);
      console.log('[TaskManagement] Data type:', Array.isArray(data) ? 'array' : typeof data);
      console.log('[TaskManagement] Data length:', Array.isArray(data) ? data.length : 'N/A');
      
      const taskstatusArray = Array.isArray(data) ? data : [];
      console.log('[TaskManagement] Setting task status:', taskstatusArray);
      settaskStatus(taskstatusArray);
    } catch (error) {
      console.error('[TaskManagement] Error fetching task status:', error);
      console.error('[TaskManagement] Error details:', error.message);
      settaskStatus([]);
    } finally {
       setLoadingTaskStatus(false);
    }
  };

  const handleOpen = async (task = null, type = 'regular') => {
    setError('');
    setSuccess('');
    setTouchedFields({});
    setCurrentErrorField(null);
    
    // Fetch Marcom users when dialog opens (if user is Marcom or admin)
    if (isUserMarcom()) {
      // Ensure Marcom users are loaded before setting form state
      await fetchMarcomUsers();
    }
    
    if (task) {
      // When editing, determine task type based on IsTask field
      if (task.IsTask === false) {
        // For non-task records, we can't easily distinguish between meeting and training
        // So we'll default to 'meeting' for editing purposes, but the form will show limited fields
        setTaskType('meeting');
      } else {
        setTaskType('regular');
      }
    } else {
      setTaskType(type);
    }
    if (task) {
      const assignedIds = task.assignees ? task.assignees.map(a => a.id) : [];
      const ownerId = task.owner?.id || '';
      
      // Handle intervenor - the model getter should return an array, but handle all cases
      let intervenorIds = [];
      console.log('[TaskManagement] Editing task - task.intervenor:', task.intervenor, 'type:', typeof task.intervenor);
      
      if (task.intervenor !== null && task.intervenor !== undefined) {
        if (Array.isArray(task.intervenor)) {
          // Already an array from model getter
          intervenorIds = task.intervenor.map(id => parseInt(id, 10)).filter(id => !isNaN(id) && id > 0);
          console.log('[TaskManagement] intervenor is array:', intervenorIds);
        } else if (typeof task.intervenor === 'number') {
          // Single INT value
          intervenorIds = [task.intervenor];
          console.log('[TaskManagement] intervenor is number:', intervenorIds);
        } else if (typeof task.intervenor === 'string' && task.intervenor.trim() !== '') {
          try {
            const parsed = JSON.parse(task.intervenor);
            if (Array.isArray(parsed)) {
              intervenorIds = parsed.map(id => parseInt(id, 10)).filter(id => !isNaN(id) && id > 0);
            } else if (typeof parsed === 'number') {
              intervenorIds = [parsed];
            }
            console.log('[TaskManagement] intervenor parsed from JSON string:', intervenorIds);
          } catch (e) {
            // If parsing fails, treat as single ID
            const singleId = parseInt(task.intervenor, 10);
            if (!isNaN(singleId) && singleId > 0) {
              intervenorIds = [singleId];
              console.log('[TaskManagement] intervenor parsed as single number:', intervenorIds);
            } else {
              console.log('[TaskManagement] intervenor string could not be parsed:', task.intervenor);
            }
          }
        } else {
          console.log('[TaskManagement] intervenor is null/undefined/empty');
        }
      } else {
        console.log('[TaskManagement] task.intervenor is null or undefined');
      }
      
      console.log('[TaskManagement] Final intervenorIds for form:', intervenorIds);
      
      const selectedOptions = intervenorIds
        .map(id => marcomUsers.find(u => parseInt(u.id,10) === id) || { id, name: `User ${id}` });

      setFormData({
        title: task.title,
        description: task.description || '',
        projectId: task.projectId || '',
        milestone: task.milestone || '',
        userStory: task.userStory || '',
        sprint: task.sprint || '',
        assignedTo: assignedIds,
        ownerId: ownerId,
        qcId: task.qcId || '',
        status: task.status || 'todo',
        priority: task.priority || 'medium',
        estimatedHours: task.estimatedHours || '',
        deadline: task.deadline ? task.deadline.split('T')[0] : '',
        estimatedTransactions: task.estimatedTransactions || '',
        transactionType: task.transactionType || '',
        filelocation: task.filelocation || '',
        intervenor: selectedOptions.map(o=>parseInt(o.id,10)),
      });
      
      // Fetch QCs and transaction types for the project's service when editing
      if (task.projectId) {
        const selectedProject = projects.find(p => p.id === task.projectId);
        if (selectedProject?.serviceId) {
          fetchQCsByService(selectedProject.serviceId);
          fetchTransactionTypes(selectedProject.serviceId);
          fetchTaskStatus(selectedProject.serviceId);
        }
      }
      setEditId(task.id);
      
      // When editing a task, use optimized function with projectId
      const allAssignedIds = [...assignedIds];
      if (ownerId) allAssignedIds.push(ownerId);
      fetchUsersOptimized(allAssignedIds, task.projectId);
    } else {
      // Set default values based on task type
      const defaultFormData = {
        title: '',
        description: '',
        projectId: '',
        milestone: '',
        userStory: '',
        sprint: '',
        assignedTo: [],
        ownerId: user.id,
        qcId: '',
        status: 'todo',
        priority: 'medium',
        estimatedHours: '',
        deadline: '',
        estimatedTransactions: '',
        transactionType: '',
        filelocation: '',
        intervenor: [],
      };

      // For meetings and trainings, set hidden fields to null
      if (type === 'meeting' || type === 'training') {
        defaultFormData.status = null;
        defaultFormData.priority = null;
        defaultFormData.estimatedHours = null;
        defaultFormData.deadline = null;
        defaultFormData.estimatedTransactions = null;
        defaultFormData.transactionType = null;
      }

      setFormData(defaultFormData);
      setEditId(null);
      
      // Fetch users for new tasks using optimized function (no projectId initially)
      fetchUsersOptimized([], null);
    }
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Sequential required validation
    const firstMissing = getFirstMissingRequired(formData);
    if (firstMissing) {
      setTouchedFields({ ...touchedFields, [firstMissing]: true });
      setCurrentErrorField(firstMissing);
      setError('Title, project, description, owner, and assignee are required');
      return;
    }

    // Validate that Assignees and Intervenor don't have the same user
    if (!isFieldHidden('intervenor') && Array.isArray(formData.intervenor) && formData.intervenor.length > 0) {
      const assignedToIds = Array.isArray(formData.assignedTo) 
        ? formData.assignedTo.map(id => parseInt(id, 10)).filter(id => !isNaN(id))
        : [];
      const intervenorIds = formData.intervenor.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      
      const duplicates = assignedToIds.filter(id => intervenorIds.includes(id));
      if (duplicates.length > 0) {
        setError('Assignee and Intervenor cannot contain the same user. Please remove duplicates.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const method = editId ? 'PUT' : 'POST';
      const url = editId
        ? `${process.env.REACT_APP_API_URL}/tasks/${editId}`
        : `${process.env.REACT_APP_API_URL}/tasks`;
      
      // Prepare submission data based on task type
      // Exclude hidden fields from submission
      const submissionDataBase = { ...formData };
      hiddenFields.forEach(field => {
        delete submissionDataBase[field];
      });
      
      let submissionData = {
        ...submissionDataBase,
        assignedTo: formData.assignedTo,
        ownerId: (!isFieldHidden('ownerId') && (user.role === 'admin' || user.role === 'project_manager')) ? formData.ownerId : undefined,
        IsTask: taskType === 'regular', // true for regular tasks, false for meetings and training
      };

      // For meetings and trainings, set hidden fields to null
      if (taskType === 'meeting' || taskType === 'training') {
        submissionData = {
          ...submissionData,
          status: null,
          priority: null,
          estimatedHours: null,
          deadline: null,
          estimatedTransactions: null,
          transactionType: null,
        };
      } else {
        // For regular tasks, include all fields
        submissionData = {
          ...submissionData,
          estimatedHours: parseFloat(formData.estimatedHours) || 0,
          estimatedTransactions: parseInt(formData.estimatedTransactions) || 0,
          transactionType: formData.transactionType,
        };
      }
      
      // Add intervenor if visible (Marcom users only)
      if (!isFieldHidden('intervenor')) {
        submissionData.intervenor = Array.isArray(formData.intervenor) && formData.intervenor.length > 0 
          ? formData.intervenor.map(id => parseInt(id, 10)).filter(id => !isNaN(id))
          : null;
      }
        
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(submissionData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${editId ? 'update' : 'create'} task`);
      }
      
      let msg;
      if (editId) {
        msg = 'Task updated successfully!';
      } else {
        if (taskType === 'meeting') {
          msg = 'Meeting created successfully!';
        } else if (taskType === 'training') {
          msg = 'Training created successfully!';
        } else {
          msg = 'Task created successfully!';
        }
      }
      setSnackbarMsg(msg);
      setSnackbarOpen(true);
      handleClose();
      setPage(0); // reset to first page after task creation
      fetchTasks();
      fetchDistinctTaskFields();
    } catch (error) {
      console.error(`Error ${editId ? 'updating' : 'creating'} task:`, error);
      setSnackbarMsg(error.message);
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...formData };
    
    if (name === 'projectId') {
      const projectIdNum = typeof value === 'string' ? parseInt(value, 10) : value;
      const selectedProject = projects.find(p => p.id === projectIdNum || p.id === value);
      console.log('[TaskManagement] Project selected:', projectIdNum);
      console.log('[TaskManagement] Selected project object:', selectedProject);
      console.log('[TaskManagement] Project serviceId:', selectedProject?.serviceId);
      
      updated = {
        ...formData,
        projectId: value,
        // Clear owner, assignees, QC, and transaction type when project changes
        ownerId: '',
        assignedTo: [],
        qcId: '',
        transactionType: '',
      };
      // Fetch users when project is selected (optimized with projectId)
      fetchUsersOptimized([], value);
      // Fetch QCs for the project's service
      if (selectedProject?.serviceId) {
        console.log('[TaskManagement] Project has serviceId, fetching QCs and transaction types');
        fetchQCsByService(selectedProject.serviceId);
        // Fetch transaction types for the project's service
        fetchTransactionTypes(selectedProject.serviceId);
        fetchTaskStatus(selectedProject.serviceId);
      } else {
        console.log('[TaskManagement] Project does not have serviceId, clearing QCs and transaction types');
        setQcUsers([]);
        setTransactionTypes([]);
        settaskStatus([]);
      }
    } else if (name === 'qcId') {
      updated = { ...formData, qcId: value };
    } else if (name === 'assignedTo') {
      // MUI multi-select returns string[] or number[]
      updated = {
        ...formData,
        assignedTo: typeof value === 'string' ? value.split(',') : value,
      };
    } else if (name === 'ownerId') {
      updated = { ...formData, ownerId: value };
    } else if (name === 'intervenor') {
      // Handle multiselect intervenor - value is array of user objects or IDs
      const intervenorIds = Array.isArray(value) ? value.map(u=>parseInt(u.id)).filter(id=>!isNaN(id)) : [];
      // Remove overlaps with assignees
      const filtered = intervenorIds.filter(id=>!formData.assignedTo.map(a=>parseInt(a,10)).includes(id));
      updated = { ...formData, intervenor: filtered };
    } else {
      updated = {
        ...formData,
        [name]: value,
      };
    }
    
    setFormData(updated);
    // Mark field as touched
    const newTouched = { ...touchedFields, [name]: true };
    setTouchedFields(newTouched);
    // Advance sequential error if the current one is fixed
    const firstMissing = getFirstMissingRequired(updated);
    setCurrentErrorField(firstMissing);
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
    setSuccess('');
    setEditId(null);
    setTaskType('regular');
    setTouchedFields({});
    setCurrentErrorField(null);
    setFormData({
      title: '',
      description: '',
      projectId: '',
      milestone: '',
      userStory: '',
      sprint: '',
      assignedTo: [],
      ownerId: user.id,
      qcId: '',
      status: 'todo',
      priority: 'medium',
      estimatedHours: '',
      deadline: '',
      estimatedTransactions: '',
      transactionType: '',
      filelocation: '',
      intervenor: [],
    });
    setQcUsers([]);
  };

  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tasks/${taskToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete task');
      }

      setSuccess('Task deleted successfully!');
      setDeleteOpen(false);
      setTaskToDelete(null);
      fetchTasks();

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting task:', error);
      setError(error.message);
      setDeleteOpen(false);
      setTaskToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setTaskToDelete(null);
  };

  const formatStatusLabel = (status) => {
    if (!status) return 'Not Set';
    // Special case: "todo" should display as "To Do"
    if (status.toLowerCase() === 'todo') return 'To Do';
    // Convert snake_case to Title Case
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (dateString, isMeetingOrTraining = false) => {
    if (!dateString) return isMeetingOrTraining ? 'N/A' : 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  // Helper function to determine if a task is a meeting or training
  const isMeetingOrTraining = (task) => {
    return task.status === null && task.priority === null && task.estimatedHours === null;
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      taskName: '',
      projectName: '',
      status: '',
      serviceId: ''
    });
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  function descendingComparator(a, b, prop) {
    if (b[prop] < a[prop]) return -1;
    if (b[prop] > a[prop]) return 1;
    return 0;
  }

  // Helper to calculate total column count for colSpan
  const getTotalColumnCount = () => {
    let count = 10; // Base columns: Task ID, Task Name, Project, Milestone, User Story, Sprint, Task Description, Est. Hours, Assigned To, Status
    if (['project_manager', 'admin', 'team_leader', 'team_member'].includes(user?.role)) count += 2; // Actual Hours, Variance
    if (isUserMarcom()) count += 1; // Intervenor column
    if (shouldShowRejectionColumns()) count += 2; // Rejection Reason, No. of Rejections
    if (!(isUserMarcom() && user?.role !== 'admin')) count += 2; // Priority, Deadline
    if (isUserMarcom() && !isFieldHidden('filelocation')) count += 1; // File Location
    if (canEdit || canDelete) count += 1; // Actions
    return count;
  };

  function getComparator(ord, prop) {
    return ord === 'desc'
      ? (a, b) => descendingComparator(a, b, prop)
      : (a, b) => -descendingComparator(a, b, prop);
  }

  function stableSort(array, comparator) {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
      const orderRes = comparator(a[0], b[0]);
      if (orderRes !== 0) return orderRes;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  }

  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Filter tasks based on current filters
  const filteredTasks = tasks.filter(task => {
    // Task Name filter
    if (filters.taskName && !task.title.toLowerCase().includes(filters.taskName.toLowerCase())) {
      return false;
    }
    
    // Project Name filter
    if (filters.projectName && task.Project?.name && !task.Project.name.toLowerCase().includes(filters.projectName.toLowerCase())) {
      return false;
    }
    
    // Status filter
    if (filters.status && task.status !== filters.status) {
      return false;
    }
    
    // Service filter (only for admin)
    if (filters.serviceId && user?.role === 'admin') {
      const projectServiceId = task.Project?.serviceId ? parseInt(task.Project.serviceId, 10) : null;
      const filterServiceId = parseInt(filters.serviceId, 10);
      if (projectServiceId !== filterServiceId) {
        return false;
      }
    }
    
    return true;
  });

  // Client-side pagination on filtered results
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedFilteredTasks = filteredTasks.slice(startIndex, endIndex);

  // Helper: Marcom non-admin
  const isMarcomNonAdmin = isUserMarcom() && user?.role !== 'admin';

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>Task Management</Typography>
      {/* Filter Section — Notion-style soft bar, no "Filters" title */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
          border: (t) => t.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          borderRadius: 2.5,
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          {/* Status Filter — primary, always visible */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="todo">To Do</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="submitted_for_review">Submitted for Review</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="Waiting for Approved Copy">Waiting for Approved Copy</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1, minWidth: 0 }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            <ToolbarButton
              variant={showAdvancedFilters ? 'contained' : 'outlined'}
              startIcon={<TuneIcon />}
              onClick={() => setShowAdvancedFilters((v) => !v)}
            >
              More filters
              {(filters.projectName || filters.taskName || filters.serviceId) && ' •'}
            </ToolbarButton>
            {(filters.taskName || filters.projectName || filters.status || filters.serviceId) && (
              <ToolbarButton
                variant="outlined"
                onClick={clearFilters}
              >
                Clear All
              </ToolbarButton>
            )}
          </Box>
        </Box>

        {(showAdvancedFilters || filters.projectName || filters.taskName || filters.serviceId) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
              mt: 1.5,
              pt: 1.5,
              borderTop: (t) => t.palette.mode === 'dark' ? '1px dashed rgba(255,255,255,0.08)' : '1px dashed #e5e7eb',
            }}
          >
            {/* Project Name Filter - Autocomplete */}
            <Autocomplete
              size="small"
              options={[...new Set(tasks.map(t => t.Project?.name).filter(Boolean))].sort()}
              value={filters.projectName}
              onChange={(event, newValue) => {
                handleFilterChange('projectName', newValue || '');
                // Clear task filter if it doesn't belong to the newly selected project
                if (newValue && filters.taskName) {
                  const taskBelongs = tasks.some(t =>
                    t.Project?.name === newValue && t.title === filters.taskName
                  );
                  if (!taskBelongs) handleFilterChange('taskName', '');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project Name"
                  placeholder="Select or type a project..."
                />
              )}
              sx={{ minWidth: 200 }}
              freeSolo
              clearOnEscape
            />

            {/* Task Name Filter - Autocomplete (filtered by selected project) */}
            <Autocomplete
              size="small"
              options={[...new Set(
                tasks
                  .filter(t => !filters.projectName || t.Project?.name === filters.projectName)
                  .map(t => t.title)
                  .filter(Boolean)
              )].sort()}
              value={filters.taskName}
              onChange={(event, newValue) => handleFilterChange('taskName', newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Task Name"
                  placeholder={filters.projectName ? 'Select a task in this project...' : 'Select or type a task...'}
                />
              )}
              sx={{ minWidth: 200 }}
              freeSolo
              clearOnEscape
            />

            {/* Service Filter - Only for Admin */}
            {user?.role === 'admin' && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Service</InputLabel>
                <Select
                  value={filters.serviceId}
                  onChange={(e) => handleFilterChange('serviceId', e.target.value)}
                  label="Service"
                  disabled={loadingServices}
                >
                  <MenuItem value="">All Services</MenuItem>
                  {services.map(service => (
                    <MenuItem key={service.Id} value={service.Id.toString()}>
                      {service.Service}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </Box>
      {canAdd && (
        <Box sx={{ mb: 3, mt: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToolbarButton
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen(null, 'regular')}
          >
            Add New Task
          </ToolbarButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
            <ToolbarButton
              variant="outlined"
              startIcon={<Add />}
              onClick={() => handleOpen(null, 'meeting')}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Add New Meeting
            </ToolbarButton>
            <ToolbarButton
              variant="outlined"
              startIcon={<Add />}
              onClick={() => handleOpen(null, 'training')}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Add New Training
            </ToolbarButton>
            <ToolbarButton
              variant="outlined"
              startIcon={<Download />}
              onClick={() => downloadCsvTemplate('tasks_template.csv', TASK_TEMPLATE_COLUMNS)}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Download Template
            </ToolbarButton>
            {canAdd && (
              <ToolbarButton
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => setBulkGridOpen(true)}
                sx={{
                  color: 'text.secondary',
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                  '&:hover': {
                    borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                    backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                  },
                }}
              >
                Add Many Tasks
              </ToolbarButton>
            )}
          </Box>
        </Box>
      )}
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose} message={snackbarMsg} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />
      
      {permissionsLoading ? (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255, 255, 255, 0.1)' 
              : '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <WaveLoading theme={theme} size="large" />
        </Box>
      ) : !canView ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            You don't have permission to view this data.
          </Typography>
        </Box>
      ) : (
      <TableContainer
        component={Paper}
        sx={{
          background: theme.palette.mode === 'dark'
            ? 'rgba(44, 48, 52, 0.6)'
            : 'rgba(255, 255, 255, 0.6)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          overflowX: 'auto',
          overflowY: 'hidden',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.3)' 
              : 'rgba(0, 0, 0, 0.3)',
            borderRadius: 4,
            '&:hover': {
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.5)' 
                : 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Table sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1976d2' }}>
              <TableCell sortDirection={orderBy === 'title' ? order : false} sx={{ fontWeight: 'bold', minWidth: 240, maxWidth: 340 }}>
                <TableSortLabel active={orderBy === 'title'} direction={orderBy === 'title' ? order : 'asc'} onClick={() => handleSort('title')} sx={{ color: 'inherit' }}>Task</TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Iteration</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 220, maxWidth: 320 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Hours</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Assigned To</TableCell>
              {isUserMarcom() && (
                <TableCell sx={{ fontWeight: 'bold' }}>Intervenor</TableCell>
              )}
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              {shouldShowRejectionColumns() && (
                <>
                  <TableCell sx={{ fontWeight: 'bold' }}>Rejection Reason</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>No. of Rejections</TableCell>
                </>
              )}
              {!isMarcomNonAdmin && (
                <>
                  <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Deadline</TableCell>
                </>
              )}
              {isUserMarcom() && !isFieldHidden('filelocation') && (
                <TableCell sx={{ fontWeight: 'bold', minWidth: 300, maxWidth: 300 }}>File Location</TableCell>
              )}
              {(canEdit || canDelete) && <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingTasks ? (
              <TableRow>
                <TableCell 
                  colSpan={getTotalColumnCount()}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <WaveLoading theme={theme} size="large" />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 500
                      }}
                    >
                      Loading tasks...
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        maxWidth: 300,
                        textAlign: 'center'
                      }}
                    >
                      Please wait while we fetch the latest task data
                    </Typography>
                  </Box>
                </TableCell>
                  </TableRow>
                )
              : stableSort(paginatedFilteredTasks, getComparator(order, orderBy))
                  .map((task) => (
                    <TableRow
                      key={task.id}
                      hover
                      sx={{
                        borderBottom: theme.palette.mode === 'dark'
                          ? '1px solid rgba(255, 255, 255, 0.05)'
                          : '1px solid rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                        }
                      }}
                    >
                      {/* Task — title + small TASK-id subtitle */}
                      <TableCell sx={{
                        color: theme.palette.mode === 'dark' ? 'white' : '#2C3034',
                        fontWeight: 500,
                        minWidth: 240,
                        maxWidth: 340,
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {task.title || '-'}
                          </Box>
                          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 400, mt: 0.25 }}>
                            TASK-{task.id}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                        fontWeight: 400,
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                          <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {task.Project?.name || 'No Project'}
                          </Box>
                          {(() => {
                            const k = getProjectDisplayKey(task.Project);
                            return k ? (
                              <Box sx={{
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                mt: 0.25,
                              }}>
                                {k}
                              </Box>
                            ) : null;
                          })()}
                        </Box>
                      </TableCell>
                      {/* Iteration — milestone · user story · sprint, collapsed */}
                      <TableCell sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}>
                        {(() => {
                          const parts = [task.milestone, task.userStory, task.sprint].filter(Boolean);
                          if (parts.length === 0) return '—';
                          return (
                            <Tooltip
                              title={
                                <Box>
                                  {task.milestone && <Box>Milestone: {task.milestone}</Box>}
                                  {task.userStory && <Box>User Story: {task.userStory}</Box>}
                                  {task.sprint && <Box>Sprint: {task.sprint}</Box>}
                                </Box>
                              }
                              placement="top"
                            >
                              <Box component="span" sx={{ cursor: 'help' }}>
                                {parts.join(' · ')}
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                        fontWeight: 400,
                        minWidth: 220,
                        maxWidth: 320,
                      }}>
                        <Tooltip title={task.description || ''} placement="top-start">
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                              <a target="_blank" rel="noopener noreferrer" href={decoratedHref} key={key} style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                                {decoratedText}
                              </a>
                            )}>
                              {task.description || ''}
                            </Linkify>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {/* Hours — Est / Actual (variance), all in one cell */}
                      <TableCell sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}>
                        {(() => {
                          const showActual = ['project_manager', 'admin', 'team_leader', 'team_member'].includes(user?.role);
                          const est = task.estimatedHours !== null && task.estimatedHours !== undefined ? task.estimatedHours : null;
                          if (!showActual) {
                            return est ?? '—';
                          }
                          const estNum = parseFloat(task.estimatedHours) || 0;
                          const actual = parseFloat(task.actualHours ?? task.totalHours) || 0;
                          const variance = actual - estNum;
                          const varianceColor = variance > 0
                            ? theme.palette.error.main
                            : variance === 0
                              ? 'text.secondary'
                              : theme.palette.success.main;
                          const isEdited = task.actualHoursEdited === true;
                          return (
                            <Tooltip
                              title={
                                <Box>
                                  <Box>Estimated: {est ?? '—'}h</Box>
                                  <Box>Actual: {actual}h{isEdited ? ' (edited)' : ''}</Box>
                                  <Box>Variance: {variance > 0 ? `+${variance}` : variance}h</Box>
                                </Box>
                              }
                              placement="top"
                            >
                              <Box component="span" sx={{ cursor: 'help' }}>
                                {est ?? 0} / {actual}
                                <Box component="span" sx={{ ml: 0.75, fontSize: '0.8125rem', color: varianceColor, fontWeight: 500 }}>
                                  ({variance > 0 ? `+${variance}` : variance})
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                        fontWeight: 400
                      }}>
                        {task.assignees && task.assignees.length
                          ? task.assignees.map(u => u.name).join(', ')
                          : task.assignee?.name || 'Unassigned'}
                      </TableCell>
                      {isUserMarcom() && (
                        <TableCell sx={{ 
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400
                        }}>
                          {(() => {
                            // Parse intervenor - handle array, JSON string, or single number
                            let intervenorIds = [];
                            if (task.intervenor) {
                              if (Array.isArray(task.intervenor)) {
                                intervenorIds = task.intervenor.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
                              } else if (typeof task.intervenor === 'string') {
                                try {
                                  const parsed = JSON.parse(task.intervenor);
                                  intervenorIds = Array.isArray(parsed) 
                                    ? parsed.map(id => parseInt(id, 10)).filter(id => !isNaN(id))
                                    : (typeof parsed === 'number' ? [parsed] : []);
                                } catch (e) {
                                  const singleId = parseInt(task.intervenor, 10);
                                  if (!isNaN(singleId)) intervenorIds = [singleId];
                                }
                              } else if (typeof task.intervenor === 'number') {
                                intervenorIds = [task.intervenor];
                              }
                            }
                            
                            // Map IDs to names using marcomUsers
                            if (intervenorIds.length > 0 && marcomUsers.length > 0) {
                              const intervenorNames = intervenorIds
                                .map(id => {
                                  const user = marcomUsers.find(u => parseInt(u.id, 10) === id);
                                  return user ? user.name : `User ${id}`;
                                })
                                .filter(name => name);
                              return intervenorNames.length > 0 ? intervenorNames.join(', ') : '-';
                            }
                            return '-';
                          })()}
                        </TableCell>
                      )}
                      <TableCell sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                        fontWeight: 400
                      }}>
                        <StatusPill status={task.status} />
                      </TableCell>
                      {shouldShowRejectionColumns() && (
                        <>
                          <TableCell sx={{ 
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                            fontWeight: 400,
                            maxWidth: 300,
                          }}>
                            {task.status === 'rejected' && task.rejectionReason ? (
                              <Tooltip title={task.rejectionReason} arrow>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: theme.palette.error.main,
                                    fontStyle: 'italic'
                                  }}
                                >
                                  {task.rejectionReason}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {task.status === 'rejected' ? 'N/A' : '-'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ 
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {task.rejectionCount || 0}
                          </TableCell>
                        </>
                      )}
                      {!isMarcomNonAdmin && (
                        <>
                          <TableCell sx={{ 
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                            fontWeight: 400
                          }}>
                            <StatusPill status={task.priority} />
                          </TableCell>
                          <TableCell sx={{
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                            fontWeight: 400
                          }}>{formatDate(task.deadline, isMeetingOrTraining(task))}</TableCell>
                        </>
                      )}
                      {isUserMarcom() && !isFieldHidden('filelocation') && (
                        <TableCell sx={{ 
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400,
                          minWidth: 300,
                          maxWidth: 300,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                            <a target="_blank" rel="noopener noreferrer" href={decoratedHref} key={key} style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                              {decoratedText}
                            </a>
                          )}>
                            {task.filelocation || '-'}
                          </Linkify>
                        </TableCell>
                      )}
                      {(canEdit || canDelete) && (
                        <TableCell sx={{ 
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400
                        }}>
                          {canEdit && (
                            <Tooltip title="Edit">
                              <IconButton
                                onClick={() => handleOpen(task)}
                                sx={{
                                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#2C3034',
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                                  }
                                }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={() => handleDeleteClick(task)}
                                sx={{
                                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#2C3034',
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                                  }
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
            }
            {!loadingTasks && filteredTasks.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={getTotalColumnCount()}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 500
                      }}
                    >
                      No tasks found
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        maxWidth: 300,
                        textAlign: 'center'
                      }}
                    >
                      Try adjusting your search or add a new task
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={Math.max(0, filteredTasks.length)}
          page={Math.max(0, Math.min(page, Math.max(0, Math.ceil(filteredTasks.length / rowsPerPage) - 1)))}
          onPageChange={handleChangePage}
          rowsPerPage={Math.max(1, Math.min(100, rowsPerPage))}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          showFirstButton
          showLastButton
        />
      </TableContainer>
      )}

      {/* Add/Edit Task Dialog */}
      <Dialog 
        open={open} 
        onClose={(event, reason) => {
          // Only close on explicit actions (Cancel button, etc.), not on backdrop click or Escape key
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleClose();
          }
        }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editId ? 
           (taskType === 'meeting' ? 'Edit Meeting' :
            taskType === 'training' ? 'Edit Training' : 'Edit Task') :
           taskType === 'meeting' ? 'Add New Meeting' :
           taskType === 'training' ? 'Add New Training' : 'Add New Task'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          {!isFieldHidden('projectId') && (
            <FormControl fullWidth margin="normal">
              <Autocomplete
                options={getFilteredProjects()}
                getOptionLabel={(option) => {
                  const k = getProjectDisplayKey(option);
                  return k ? `${k} · ${option.name}` : option.name;
                }}
                renderOption={(props, option) => {
                  const k = getProjectDisplayKey(option);
                  return (
                    <Box component="li" {...props} key={option.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', gap: 0.25, py: 1 }}>
                      <Box sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{option.name}</Box>
                      {k && (
                        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                          {k}
                        </Box>
                      )}
                    </Box>
                  );
                }}
                value={getFilteredProjects().find(project => project.id === formData.projectId) || null}
                onChange={(event, newValue) => {
                  const selectedProject = newValue;
                  const updated = {
                    ...formData,
                    projectId: newValue ? newValue.id : '',
                    qcId: '', // Clear QC when project changes
                  };
                  setFormData(updated);
                  setTouchedFields({ ...touchedFields, projectId: true });
                  const firstMissing = getFirstMissingRequired(updated);
                  setCurrentErrorField(firstMissing);
                  // Fetch users when project is selected (optimized with projectId)
                  fetchUsersOptimized([], newValue ? newValue.id : null);
                  // Fetch QCs for the project's service
                  if (newValue?.serviceId) {
                    fetchQCsByService(newValue.serviceId);
                    // Fetch transaction types for the project's service
                    fetchTransactionTypes(newValue.serviceId);
                    fetchTaskStatus(newValue.serviceId);
                  } else {
                    setQcUsers([]);
                    setTransactionTypes([]);
                    settaskStatus([]);
                  }
                }}
                disabled={user?.role === 'partner' && editId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project"
                    placeholder="Search for project..."
                    required
                    error={currentErrorField === 'projectId'}
                    helperText={currentErrorField === 'projectId' ? "Project is required" : ""}
                  />
                )}
              />
            </FormControl>
          )}
          {!isFieldHidden('milestone') && (taskType === 'regular' || (editId && tasks.find(t => t.id === editId)?.IsTask === true)) && (
            <Autocomplete
              freeSolo
              fullWidth
              options={milestoneOptions}
              value={formData.milestone || ''}
              onChange={(event, newValue) => {
                setFormData({ ...formData, milestone: newValue || '' });
              }}
              onInputChange={(event, newValue) => {
                setFormData({ ...formData, milestone: newValue || '' });
              }}
              disabled={user?.role === 'partner' && editId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label="Milestone"
                  placeholder="Select or type a milestone..."
                />
              )}
            />
          )}
          {!isFieldHidden('userStory') && (taskType === 'regular' || (editId && tasks.find(t => t.id === editId)?.IsTask === true)) && (
            <Autocomplete
              freeSolo
              fullWidth
              options={userStoryOptions}
              value={formData.userStory || ''}
              onChange={(event, newValue) => {
                setFormData({ ...formData, userStory: newValue || '' });
              }}
              onInputChange={(event, newValue) => {
                setFormData({ ...formData, userStory: newValue || '' });
              }}
              disabled={user?.role === 'partner' && editId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label="User Story"
                  placeholder="Select or type a user story..."
                />
              )}
            />
          )}
          {!isFieldHidden('sprint') && (taskType === 'regular' || (editId && tasks.find(t => t.id === editId)?.IsTask === true)) && (
            <Autocomplete
              freeSolo
              fullWidth
              options={sprintOptions}
              value={formData.sprint || ''}
              onChange={(event, newValue) => {
                setFormData({ ...formData, sprint: newValue || '' });
              }}
              onInputChange={(event, newValue) => {
                setFormData({ ...formData, sprint: newValue || '' });
              }}
              disabled={user?.role === 'partner' && editId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label="Sprint"
                  placeholder="Select or type a sprint..."
                />
              )}
            />
          )}
          {taskType === 'meeting' && !editId ? (
            <FormControl fullWidth margin="normal">
              <InputLabel>Meeting Type</InputLabel>
              <Select
                name="title"
                value={formData.title}
                onChange={handleChange}
                label="Meeting Type"
                required
              >
                {meetingLookup.map((meeting) => (
                  <MenuItem key={meeting.Id} value={meeting.Meeting_type}>
                    {meeting.Meeting_type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : taskType === 'training' && !editId ? (
            <FormControl fullWidth margin="normal">
              <InputLabel>Training Type</InputLabel>
              <Select
                name="title"
                value={formData.title}
                onChange={handleChange}
                label="Training Type"
                required
              >
                {trainingLookup.map((training) => (
                  <MenuItem key={training.Id} value={training.Training_type}>
                    {training.Training_type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            !isFieldHidden('title') && (
              <TextField
                autoFocus
                margin="normal"
                name="title"
                label="Task Name"
                fullWidth
                required
                value={formData.title}
                onChange={handleChange}
                error={currentErrorField === 'title'}
                helperText={currentErrorField === 'title' ? "Task name is required" : ""}
                disabled={user?.role === 'partner' && editId}
                InputProps={{ readOnly: user?.role === 'partner' && editId }}
              />
            )
          )}
          {!isFieldHidden('description') && (
            <TextField
              margin="normal"
              name="description"
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange}
              error={currentErrorField === 'description'}
              helperText={currentErrorField === 'description' ? "Description is required" : ""}
              disabled={user?.role === 'partner' && editId}
              InputProps={{ readOnly: user?.role === 'partner' && editId }}
            />
          )}
          {/* QC Dropdown - Only show for Marcom users/admins on regular tasks when project is selected */}
          {isUserMarcom() && !isFieldHidden('qcId') && (taskType === 'regular' || (editId && tasks.find(t => t.id === editId)?.IsTask === true)) && formData.projectId && (
            <FormControl fullWidth margin="normal">
              <Autocomplete
                options={qcUsers}
                getOptionLabel={(option) => option.name || option.email || 'Unknown'}
                value={qcUsers.find(qc => qc.id === parseInt(formData.qcId)) || null}
                onChange={(event, newValue) => {
                  const updated = { ...formData, qcId: newValue ? newValue.id : '' };
                  setFormData(updated);
                  setTouchedFields({ ...touchedFields, qcId: true });
                }}
                disabled={user?.role === 'partner' && editId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="QC Reviewer"
                    placeholder="Select QC reviewer..."
                    helperText="Select a QC reviewer. Required when submitting for review."
                  />
                )}
              />
            </FormControl>
          )}
          {/* Intervenor Multiselect - Only show for Marcom users and admins */}
          {!isFieldHidden('intervenor') && (taskType === 'regular' || (editId && tasks.find(t => t.id === editId)?.IsTask === true)) && (
            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={marcomUsers || []}
                getOptionLabel={(option) => {
                  if (typeof option === 'object' && option !== null) {
                    return option.name || option.email || 'Unknown';
                  }
                  return String(option);
                }}
                isOptionEqualToValue={(option, value) => {
                  const optionId = typeof option === 'object' ? option.id : option;
                  const valueId = typeof value === 'object' ? value.id : value;
                  return optionId === valueId;
                }}
                value={marcomUsers.filter(user => {
                  const userId = parseInt(user.id);
                  return Array.isArray(formData.intervenor) && formData.intervenor.includes(userId);
                })}
                onChange={(event, newValue) => {
                  const intervenorIds = newValue.map(user => parseInt(user.id)).filter(id => !isNaN(id));
                  const updated = { ...formData, intervenor: intervenorIds };
                  setFormData(updated);
                  setTouchedFields({ ...touchedFields, intervenor: true });
                }}
                disabled={user?.role === 'partner' && editId}
                loading={loadingMarcomUsers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Intervenor"
                    placeholder={loadingMarcomUsers ? "Loading Marcom users..." : marcomUsers.length === 0 ? "No Marcom users available" : "Select intervenors..."}
                  />
                )}
                noOptionsText={loadingMarcomUsers ? "Loading..." : "No Marcom users available"}
              />
            </FormControl>
          )}
          {!isFieldHidden('ownerId') && (user.role === 'admin' || user.role === 'project_manager') && (
            <FormControl fullWidth margin="normal">
              <Autocomplete
                options={getProjectRelatedUsers()}
                getOptionLabel={(option) => option.name}
                value={getProjectRelatedUsers().find(user => user.id === parseInt(formData.ownerId)) || null}
                onChange={(event, newValue) => {
                  const updated = { ...formData, ownerId: newValue ? newValue.id : '' };
                  setFormData(updated);
                  setTouchedFields({ ...touchedFields, ownerId: true });
                  const firstMissing = getFirstMissingRequired(updated);
                  setCurrentErrorField(firstMissing);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Owner"
                    placeholder="Search for owner..."
                    required
                    error={currentErrorField === 'ownerId'}
                    helperText={currentErrorField === 'ownerId' ? "Owner is required" : ""}
                  />
                )}
              />
            </FormControl>
          )}
          {!isFieldHidden('assignedTo') && (
            <FormControl fullWidth margin="normal">
              <Autocomplete
                multiple
                options={getProjectRelatedUsers()}
                getOptionLabel={(option) => option.name}
                value={getProjectRelatedUsers().filter(user => formData.assignedTo.includes(parseInt(user.id)))}
                onChange={(event, newValue) => {
                  const updated = { ...formData, assignedTo: newValue.map(user => user.id) };
                  setFormData(updated);
                  setTouchedFields({ ...touchedFields, assignedTo: true });
                  const firstMissing = getFirstMissingRequired(updated);
                  setCurrentErrorField(firstMissing);
                }}
                disabled={user?.role === 'partner' && editId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assignees"
                    placeholder="Search for assignees..."
                    required
                    error={currentErrorField === 'assignedTo'}
                    helperText={currentErrorField === 'assignedTo' ? "At least one assignee is required" : ""}
                  />
                )}
              />
            </FormControl>
          )}
          {/* Only show additional fields for regular tasks, not for meetings and trainings */}
          {(taskType === 'regular' || (editId && tasks.find(t => t.id === editId)?.IsTask === true)) && (
            <>
              {!isFieldHidden('status') && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Status"
                    required
                    error={currentErrorField === 'status'}
                     helperText={currentErrorField === 'status' ? "Status is required" : ""}
                    disabled={loadingTaskStatus || !formData.projectId || (user?.role === 'partner' && editId)}
                  >
                    <MenuItem value=''><em>None</em></MenuItem>
                    {loadingTaskStatus ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : taskStatus.length > 0 ? (
                      taskStatus.map((type) => (
                        <MenuItem key={type.id} value={type.Value}>
                          {type.Tasktype}
                        </MenuItem>
                      ))
                    ) : formData.projectId ? (
                      <MenuItem disabled>No task status available for this service</MenuItem>
                    ) : (
                      <MenuItem disabled>Select a project first</MenuItem>
                    )}
                  </Select>
                </FormControl>

              )}
              {!isFieldHidden('priority') && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    label="Priority"
                    disabled={user?.role === 'partner' && editId}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              )}
              {!isFieldHidden('estimatedHours') && (
                <TextField
                  margin="normal"
                  name="estimatedHours"
                  label="Estimated Hours"
                  type="number"
                  fullWidth
                  value={formData.estimatedHours}
                  onChange={handleChange}
                  disabled={user?.role === 'partner' && editId}
                  InputProps={{ readOnly: user?.role === 'partner' && editId }}
                />
              )}
              {!isFieldHidden('deadline') && (
                <TextField
                  margin="normal"
                  name="deadline"
                  label="Deadline"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.deadline}
                  onChange={handleChange}
                  disabled={user?.role === 'partner' && editId}
                  InputProps={{ readOnly: user?.role === 'partner' && editId }}
                />
              )}
              {!isFieldHidden('estimatedTransactions') && (
                <TextField
                  margin="normal"
                  name="estimatedTransactions"
                  label="Estimated Transactions"
                  type="number"
                  fullWidth
                  value={formData.estimatedTransactions}
                  onChange={handleChange}
                  disabled={user?.role === 'partner' && editId}
                  InputProps={{ readOnly: user?.role === 'partner' && editId }}
                />
              )}
              {!isFieldHidden('transactionType') && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>{getTransactionTypeLabel()}</InputLabel>
                  <Select
                    name="transactionType"
                    value={formData.transactionType}
                    onChange={handleChange}
                    label={getTransactionTypeLabel()}
                    disabled={loadingTransactionTypes || !formData.projectId || (user?.role === 'partner' && editId)}
                  >
                    <MenuItem value=''><em>None</em></MenuItem>
                    {loadingTransactionTypes ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : transactionTypes.length > 0 ? (
                      transactionTypes.map((type) => (
                        <MenuItem key={type.id} value={type.transactiontype}>
                          {type.transactiontype}
                        </MenuItem>
                      ))
                    ) : formData.projectId ? (
                      <MenuItem disabled>No transaction types available for this service</MenuItem>
                    ) : (
                      <MenuItem disabled>Select a project first</MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}
              {/* File Location field - Only visible for Marcom service or admins */}
              {isUserMarcom() && !isFieldHidden('filelocation') && (
                <TextField
                  margin="normal"
                  name="filelocation"
                  label="File Location"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.filelocation}
                  onChange={handleChange}
                  error={currentErrorField === 'filelocation'}
                  helperText={
                    currentErrorField === 'filelocation'
                      ? "File location is required before submitting for review"
                      : (isUserMarcom() ? "" : "Enter file location/links (multiline supported)")
                  }
                  placeholder="Enter file location or links..."
                  disabled={user?.role === 'partner' && editId}
                  InputProps={{ readOnly: user?.role === 'partner' && editId }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} color="inherit" /> :
             (editId ? 'Save Changes' :
             taskType === 'meeting' ? 'Add Meeting' :
             taskType === 'training' ? 'Add Training' : 'Add Task')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the task "{taskToDelete?.title}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add many tasks: type, paste from Excel, or import a file */}
      {bulkGridOpen && (
        <React.Suspense fallback={null}>
        <BulkGrid
          open
          onClose={() => setBulkGridOpen(false)}
          title="Add many tasks"
          subtitle="Type, paste a block from Excel or Google Sheets, or import a .xlsx / .csv file. Everything is checked before it is added."
          columns={bulkColumns}
          validateRow={validateTaskRow}
          dedupeKey={taskDedupeKey}
          dryRun={bulkDryRun}
          onSubmit={bulkSubmit}
          submitLabel={(n) => `Add ${n} task${n === 1 ? '' : 's'}`}
          templateName="tasks_template.csv"
        />
        </React.Suspense>
      )}
    </Box>
  );
};

export default TaskManagement;
